/*!
=========================================================
* Muse Ant Design Dashboard - v1.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/muse-ant-design-dashboard
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/muse-ant-design-dashboard/blob/main/LICENSE.md)
* Coded by Creative Tim
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import React, { useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from 'react-i18next';
import { HeartFilled } from "@ant-design/icons";
import ResetPasswordModal from "../components/auth/ResetPasswordModal";
import {
  Layout,
  Menu,
  Button,
  Row,
  Col,
  Typography,
  Form,
  Input,
  Select,
  message,
  Alert,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  InstagramOutlined,
  FacebookOutlined,
  TikTokOutlined
} from "@ant-design/icons";
import signinbg from "../assets/images/SimCard.png";
import logo from "../assets/images/logo.png";
import LanguageSelector from "../components/LanguageSelector";
const { Title, Text } = Typography;
const { Option } = Select;
const { Header, Content,Footer } = Layout;

const SUPPORTED_LANGS = ['fr', 'en', 'ar', 'es', 'ur'];

const SignIn = () => {
  const { t } = useTranslation();
  const browserBaseLang = (navigator.language || '').split('-')[0].toLowerCase();
  const isBrowserLangUnsupported = !SUPPORTED_LANGS.includes(browserBaseLang);
  const [langBannerDismissed, setLangBannerDismissed] = useState(false);
  const showLangBanner = isBrowserLangUnsupported && !langBannerDismissed;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const history = useHistory();
  const [form] = Form.useForm();
  const { login, user } = useAuth();

  const handleLogin = async (values) => {
    try {
      setLoading(true);
      setError(null);
      // Utiliser le contexte d'authentification pour se connecter
      const success = await login(
        values.username.trim(),
        values.password,
       // values.role
      );

      if (!success) {
        throw new Error(t('auth.loginFailed'));
      }

      message.success(t('auth.loginSuccess'));

      // Utiliser l'utilisateur qui a été récupéré du contexte avec useAuth au niveau racine
      // Redirection selon les rôles de l'utilisateur
      let redirectPath = '/'; // Chemin par défaut
      console.log(user.authorities);
      if (user && user.authorities) {
        if (user.authorities.includes('ROLE_ADMIN')) {
          redirectPath = '/admin/dashboard';
        } else if (user.authorities.includes('ROLE_AGENCY')) {
          redirectPath = '/agency/dashboard';
        } else if (user.authorities.includes('ROLE_SUBSCRIBER')) {
          redirectPath = '/subscriber/dashboard';
        }
      } else {
        // Si les rôles ne sont pas encore disponibles, attendre un peu et actualiser la page
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
      
      history.push(redirectPath);
    } catch (err) {
      setError(t('auth.invalidCredentials'));
      console.error('Erreur de connexion:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="layout-default layout-signin">
      <Header>
        <div className="header-col header-brand">
          <img src={logo} alt="eSIMPLE Dashboard" style={{ height: '50px' }} />
        </div>
      </Header>

      <Content className="signin">
        <Row gutter={[24, 0]} justify="space-around">
          <Col
            xs={{ span: 24, offset: 0 }}
            lg={{ span: 6, offset: 2 }}
            md={{ span: 12 }}
          >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={3}>{t('auth.login')}</Title>
              <Text type="secondary">
                {t('auth.loginSubtitle')}
              </Text>
            </div>

            {showLangBanner && (
              <Alert
                message={t('auth.languageBanner')}
                description={<LanguageSelector />}
                type="info"
                showIcon
                closable
                style={{ marginBottom: 24 }}
                onClose={() => setLangBannerDismissed(true)}
              />
            )}

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                style={{ marginBottom: 24 }}
                onClose={() => setError(null)}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleLogin}
              initialValues={{ role: 'AGENCY' }}
            >
            
              <Form.Item
                name="username"
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  { required: true, message: t('validation.usernameRequired') },
                  { min: 3, message: t('validation.usernameMinLength') }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />}
                  size="large"
                  placeholder={t('auth.username')}
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: t('validation.passwordRequired') },
                  { min: 4, message: t('validation.passwordMinLength') }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />}
                  size="large"
                  placeholder={t('auth.password')}
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  {loading ? t('auth.loggingIn') : t('auth.signIn')}
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Button 
                  type="link" 
                  onClick={() => setShowResetPassword(true)}
                  style={{ padding: 0 }}
                >
                  {t('auth.forgotPassword')}
                </Button>
              </div>
              
              <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <Text>{t('auth.notRegistered')} </Text>
                <Link to="/sign-up">{t('auth.createAccount')}</Link>
              </div>
            </Form>

            <ResetPasswordModal
              visible={showResetPassword}
              onCancel={() => setShowResetPassword(false)}
            />
          </Col>
          <Col
            className="sign-img"
            style={{ padding: 12, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            xs={{ span: 24 }}
            lg={{ span: 12 }}
            md={{ span: 12 }}
          >
            <img  src={signinbg} alt="eSIMPLE" style={{ width: '50%', height: 'auto' }} />
          </Col>
        </Row>
      </Content>
      <Footer >
                  <Menu mode="horizontal" style={{visibility:"hidden"}} visibility="hidden" visible={false}>
                    <Menu.Item>{t('footer.company')}</Menu.Item>
                    <Menu.Item>{t('footer.aboutUs')}</Menu.Item>
                    <Menu.Item>{t('footer.teams')}</Menu.Item>
                    <Menu.Item>{t('footer.products')}</Menu.Item>
                    <Menu.Item>{t('footer.blogs')}</Menu.Item>
                    <Menu.Item>{t('footer.pricing')}</Menu.Item>
                  </Menu>
                  <Menu mode="horizontal" className="menu-nav-social" style={{visibility:"hidden"}} visibility="hidden" visible={false}>
                    <Menu.Item>
                      <a href="#facebook">{<FacebookOutlined />}</a>
                    </Menu.Item>
                    <Menu.Item>
                      <a href="#instagram">{<InstagramOutlined />}</a>
                    </Menu.Item>
                    <Menu.Item>
                      <a href="#tiktok">{<TikTokOutlined />}</a>
                    </Menu.Item>
                    
                    
                  </Menu>
                  
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <LanguageSelector />
                  </div>
                  
                 <div className="copyright" style={{visibility:"hidden"}} visibility="hidden" visible={false}>
            © 2025, made with
            {<HeartFilled />} by
            <a href="#pablo" className="font-weight-bold" target="_blank">
              TransaSim
            </a>
            for a better eSim experience.
          </div>
                </Footer>
    </Layout>
  );
};

export default SignIn;
